import { trapFocus, removeTrapFocus } from '@theme/focus';

class MobileNavPanel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {ResizeObserver | null} */
  #chromeBarObserver = null;

  /** @type {MediaQueryList | null} matches while the mobile header is shown (data-media, see header-mobile-query.liquid) */
  #mobileQuery = null;

  connectedCallback() {
    this.#trigger = document.querySelector('.mobile-nav-trigger');
    this.#trigger?.addEventListener('click', this.toggle);
    this.addEventListener('click', this.#onOtherDrawerTriggerClick);
    document.addEventListener('keydown', this.#onKeydown);

    this.#mobileQuery = window.matchMedia(this.dataset.media || 'all');
    this.#mobileQuery.addEventListener('change', this.#onMobileQueryChange);

    const chromeBars = new Map();
    const topBar = document.querySelector('.header-mobile__top-bar');
    const bottomBar = document.querySelector('.header-mobile__bottom-bar');
    if (topBar) chromeBars.set(topBar, '--mobile-header-height');
    if (bottomBar) chromeBars.set(bottomBar, '--mobile-bottom-bar-height');

    if (chromeBars.size) {
      this.#chromeBarObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
          const property = chromeBars.get(entry.target);
          if (property && typeof height === 'number') {
            document.documentElement.style.setProperty(property, `${height}px`);
          }
        }
      });
      for (const bar of chromeBars.keys()) this.#chromeBarObserver.observe(bar);
    }
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.toggle);
    this.removeEventListener('click', this.#onOtherDrawerTriggerClick);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#chromeBarObserver?.disconnect();
    this.#mobileQuery?.removeEventListener('change', this.#onMobileQueryChange);
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  open = () => {
    this.setAttribute('open', '');
    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('opened'));
  };

  close = () => {
    this.removeAttribute('open');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.dispatchEvent(new CustomEvent('closed'));
  };

  toggle = () => {
    this.isOpen() ? this.close() : this.open();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen()) this.close();
  };

  /**
   * Closes the drawer when the desktop header takes over (viewport grows past the breakpoint
   * or input switches to mouse/trackpad), so the scroll lock and focus trap don't linger.
   * @param {MediaQueryListEvent} event
   */
  #onMobileQueryChange = (event) => {
    if (!event.matches && this.isOpen()) this.close();
  };

  /** @param {MouseEvent} event */
  #onOtherDrawerTriggerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (this.isOpen() && target.closest('[data-hygh-drawer]')) this.close();
  };
}

if (!customElements.get('mobile-nav-panel')) {
  customElements.define('mobile-nav-panel', MobileNavPanel);
}

class MobileNavMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #activeItem = null;

  /** @type {HTMLElement | null} */
  #activeChildItem = null;

  /** @type {Element | null} */
  #panel = null;

  /** @type {HTMLElement | null} tracks which item's extras currently sit in the extras-slot (see #restoreExtras). */
  #extrasOwner = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    this.#panel = this.closest('mobile-nav-panel');
    this.#panel?.addEventListener('closed', this.#onPanelClosed);
    this.#panel?.addEventListener('opened', this.#onPanelOpened);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#panel?.removeEventListener('closed', this.#onPanelClosed);
    this.#panel?.removeEventListener('opened', this.#onPanelOpened);
  }

  /** @param {'0' | '1' | '2'} index */
  #slot(index) {
    return this.querySelector(`[data-slot="${index}"]`);
  }

  #extrasSlot() {
    return this.closest('.mobile-menu__panel')?.querySelector('[data-extras-slot]') ?? null;
  }

  /**
   * Adds `--visible` two animation frames after insertion, so the CSS
   * opacity transition reliably fires (see the class doc comment above).
   * @param {Element | null} el
   * @param {string} visibleClass
   */
  #reveal(el, visibleClass) {
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add(visibleClass);
      });
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const childTrigger = target.closest('.mobile-menu__child-trigger');
    if (childTrigger instanceof HTMLButtonElement) {
      this.#onChildTriggerClick(childTrigger);
      return;
    }

    const trigger = target.closest('.mobile-menu__trigger');
    if (trigger instanceof HTMLButtonElement) {
      this.#onTriggerClick(trigger);
    }
  };

  /** @param {HTMLButtonElement} trigger */
  #onTriggerClick(trigger) {
    const item = trigger.closest('.mobile-menu__item');
    if (!(item instanceof HTMLElement)) return;

    if (this.#activeItem === item) {
      this.#toRoot();
      return;
    }

    this.#deactivateChild();
    this.#deactivateItem();

    this.#activeItem = item;
    item.setAttribute('data-active', '');
    trigger.setAttribute('aria-expanded', 'true');


    this.#relocateExtras(item);

    const children = item.querySelector('.mobile-menu__children');
    const slot1 = this.#slot('1');
    if (children && slot1) {
      children.style.display = 'block'; 
      slot1.appendChild(children); 
    }

    this.setAttribute('data-level', '1');

    if (children) this.#reveal(children, 'mobile-menu__children--visible');
    this.#reveal(this.#extrasSlot(), 'mobile-menu__extras-slot--visible');
  }

  /** @param {HTMLButtonElement} childTrigger */
  #onChildTriggerClick(childTrigger) {
    const childItem = childTrigger.closest('.mobile-menu__child-item');
    if (!(childItem instanceof HTMLElement)) return;

    if (this.#activeChildItem === childItem) {
      this.#collapseToLevel1();
      return;
    }

    this.#deactivateChild();
    this.#activateChild(childItem);
  }

  /** @param {HTMLElement} childItem */
  #activateChild(childItem) {
    this.#activeChildItem = childItem;
    childItem.setAttribute('data-active', '');

    this.#slot('1')?.classList.add('mobile-menu__slot--scrolls');

    const grandchildren = childItem.querySelector('.mobile-menu__grandchildren');
    const slot2 = this.#slot('2');
    if (grandchildren && slot2) {
      grandchildren.style.display = 'block'; // out of its display:none resting state — see the CSS comment
      slot2.appendChild(grandchildren); // ONE-TIME placement, same as children above
    }

    this.setAttribute('data-level', '2');
    if (grandchildren) this.#reveal(grandchildren, 'mobile-menu__grandchildren--visible');
  }

  #collapseToLevel1() {
    this.#deactivateChild();
    this.setAttribute('data-level', '1'); // slot-1 (children) and slot-0 (level-0) grow back via CSS alone — neither ever moved
  }

  /** Restores the active child's grandchildren to its own markup position. */
  #deactivateChild() {
    if (!this.#activeChildItem) return;
    // Not this.#activeChildItem.querySelector(...) — grandchildren was
    // appendChild'd OUT of activeChildItem into slot-2, so it's no longer a
    // descendant there to find; it's only ever in slot-2 while active.
    const grandchildren = this.#slot('2')?.querySelector('.mobile-menu__grandchildren');
    if (grandchildren) {
      grandchildren.classList.remove('mobile-menu__grandchildren--visible');
      grandchildren.style.display = ''; // back to its display:none resting state
      this.#activeChildItem.appendChild(grandchildren);
    }
    this.#activeChildItem.removeAttribute('data-active');
    this.#activeChildItem = null;
  }

  /** Restores the active item's children to its own markup position. */
  #deactivateItem() {
    if (!this.#activeItem) return;
    const children = this.#slot('1')?.querySelector('.mobile-menu__children');
    if (children) {
      children.classList.remove('mobile-menu__children--visible');
      children.style.display = ''; // back to its display:none resting state
      this.#activeItem.appendChild(children);
    }
    this.#activeItem.querySelector('.mobile-menu__trigger')?.setAttribute('aria-expanded', 'false');
    this.#activeItem.removeAttribute('data-active');
    this.#activeItem = null;
  }

  #toRoot() {
    this.#deactivateChild();
    this.#deactivateItem();
    this.#restoreExtras();
    this.#slot('1')?.classList.remove('mobile-menu__slot--scrolls'); 
    this.setAttribute('data-level', '0');
  }

  #restoreExtras() {
    const slot = this.#extrasSlot();
    const extras = slot?.firstElementChild;
    if (extras && this.#extrasOwner) {
      this.#extrasOwner.querySelector('.mobile-menu__children')?.appendChild(extras);
    }
    slot?.classList.remove('mobile-menu__extras-slot--visible');
    this.#extrasOwner = null;
  }

  /** @param {HTMLElement} item */
  #relocateExtras(item) {
    const slot = this.#extrasSlot();
    if (!slot) return;

    this.#restoreExtras();
    const extras = item.querySelector('.mobile-menu__extras');
    if (extras) {
      slot.appendChild(extras);
      this.#extrasOwner = item;
    }
  }

  #onPanelClosed = () => {
    this.#toRoot();
    removeTrapFocus();
  };

  #onPanelOpened = () => {
    trapFocus(this);
  };
}

if (!customElements.get('mobile-nav-menu')) {
  customElements.define('mobile-nav-menu', MobileNavMenu);
}
